/**
 * Установка приложения: PWA-01 (мобильная Safari), PWA-02 (инструкция),
 * PWA-07 (браузеры с `beforeinstallprompt`) — CLIENT_WEB.md §9.4.
 *
 * Главное правило §9.4 п. 2: событие `beforeinstallprompt` ПЕРЕХВАТЫВАЕТСЯ
 * (`preventDefault`) и сохраняется; системный диалог вызывается только по
 * нажатию «Установить». Автоматический показ ЗАПРЕЩЁН.
 *
 * Признак частоты `install_prompt` — один на все три экрана, в базе
 * `app-v1-device`, store `user_scope` (§2.4). Дубля в `localStorage` нет
 * (§11.4 п. 8): там разрешён единственный ключ `theme`.
 */
import { getUserScope, setUserScope } from '@shared/db'

import { isIosLike, isStandalone } from './platform'

// Раскладка и режим запуска живут в platform.ts (CLIENT_IOS_WEBAPP.md §1.2);
// реэкспорт сохраняет прежний адрес импорта для экранов.
export { isStandalone }

/* ===================================================================== */
/* Событие браузера                                                       */
/* ===================================================================== */

/** Нестандартное событие Chromium; в типах DOM его нет. */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferredEvent: BeforeInstallPromptEvent | null = null
let installed = false
const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) listener()
}

/**
 * Ставится ДО монтирования React: событие приходит один раз и рано, и
 * пропущенное событие браузер не повторит до следующей загрузки.
 */
export function captureInstallPrompt(): void {
  globalThis.addEventListener('beforeinstallprompt', (event) => {
    // §9.4 п. 2: перехват обязателен — иначе браузер покажет свой диалог сам.
    event.preventDefault()
    deferredEvent = event as BeforeInstallPromptEvent
    notify()
  })

  globalThis.addEventListener('appinstalled', () => {
    installed = true
    deferredEvent = null
    notify()
  })
}

export function subscribeInstallState(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Показывает системный диалог. Вызывать ТОЛЬКО из обработчика жеста:
 * без жеста браузер отклоняет `prompt()`.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferredEvent
  if (!event) return 'unavailable'

  try {
    await event.prompt()
    const choice = await event.userChoice
    // Событие одноразовое: повторный `prompt()` браузер отклоняет.
    deferredEvent = null
    notify()
    return choice.outcome
  } catch {
    // Жест не распознан или событие устарело — не ошибка приложения (PWA-07).
    return 'unavailable'
  }
}

/* ===================================================================== */
/* Раскладка                                                              */
/* ===================================================================== */

/**
 * §0.3 п. 3: возможности определяются feature detection, и единственное
 * исключение — инструкция по установке. В мобильной Safari нет
 * `beforeinstallprompt`, поэтому определять «можно ли установить» нечем,
 * и движок приходится распознавать явно.
 */
export function isMobileSafari(): boolean {
  if (!isIosLike()) return false

  // На iOS все браузеры — обёртки над WebKit, но установка на «Домой»
  // доступна из Safari; Chrome/Firefox/Edge на iOS её не предлагают.
  return !/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser/.test(navigator.userAgent)
}

/* ===================================================================== */
/* Частота показа                                                         */
/* ===================================================================== */

const INSTALL_PROMPT_KEY = 'install_prompt'

const DAY_MS = 24 * 60 * 60 * 1000
/** PWA-01 DoD п. 2: не чаще одного показа в 7 дней. */
const SAFARI_COOLDOWN_MS = 7 * DAY_MS
/** PWA-07 §9.4 п. 2: «Позже» скрывает баннер на 14 дней. */
const CHROMIUM_COOLDOWN_MS = 14 * DAY_MS
/** Обоим экранам — не более трёх показов за всё время. */
const MAX_SHOWS = 3
/** PWA-07 DoD п. 2: не раньше третьего сеанса. */
const MIN_SESSIONS = 3

export interface InstallPromptRecord {
  shown_count: number
  dismissed_at: string | null
  hidden_forever: boolean
  session_count: number
}

const EMPTY: InstallPromptRecord = {
  shown_count: 0,
  dismissed_at: null,
  hidden_forever: false,
  session_count: 0,
}

export async function readInstallPrompt(userId: string): Promise<InstallPromptRecord> {
  const stored = await getUserScope<Partial<InstallPromptRecord>>(userId, INSTALL_PROMPT_KEY)
  return { ...EMPTY, ...(stored ?? {}) }
}

async function write(userId: string, patch: Partial<InstallPromptRecord>): Promise<InstallPromptRecord> {
  const next = { ...(await readInstallPrompt(userId)), ...patch }
  await setUserScope(userId, INSTALL_PROMPT_KEY, next)
  return next
}

/** Один вызов на запуск приложения — счётчик сеансов для правила «не раньше третьего». */
export async function countInstallSession(userId: string): Promise<InstallPromptRecord> {
  const current = await readInstallPrompt(userId)
  return write(userId, { session_count: current.session_count + 1 })
}

/** «Позже» и крестик: скрыть до следующего допустимого показа. */
export async function dismissInstallPrompt(userId: string): Promise<InstallPromptRecord> {
  const current = await readInstallPrompt(userId)
  return write(userId, {
    dismissed_at: new Date().toISOString(),
    shown_count: current.shown_count + 1,
  })
}

/** «Уже установил(а)» и `appinstalled`: больше не предлагать никогда. */
export async function hideInstallPromptForever(userId: string): Promise<InstallPromptRecord> {
  return write(userId, { hidden_forever: true })
}

/** PWA-02 п. 1: закрытие инструкции по «Понятно» засчитывается как показ. */
export async function countInstallPromptShow(userId: string): Promise<InstallPromptRecord> {
  const current = await readInstallPrompt(userId)
  return write(userId, { shown_count: current.shown_count + 1 })
}

export type InstallVariant =
  /** PWA-07: есть перехваченное событие — системный диалог возможен. */
  | 'chromium'
  /** PWA-01: мобильная Safari — только инструкция PWA-02. */
  | 'safari'
  /** Ни того, ни другого: экран не существует (§9.4 п. 2, последний пункт). */
  | 'none'

export function installVariant(): InstallVariant {
  if (isStandalone() || installed) return 'none'
  if (deferredEvent) return 'chromium'
  if (isMobileSafari()) return 'safari'
  return 'none'
}

/**
 * Разрешает ли правило частоты показать баннер сейчас.
 * Контекст показа (корневой экран, отсутствие блокировок, набор сообщения)
 * решает вызывающий: он про интерфейс, а не про частоту.
 */
export function mayShowBanner(record: InstallPromptRecord, variant: InstallVariant): boolean {
  if (variant === 'none') return false
  if (record.hidden_forever) return false
  if (record.shown_count >= MAX_SHOWS) return false

  // PWA-07 DoD п. 2: предложение не появляется в первые два сеанса.
  if (variant === 'chromium' && record.session_count < MIN_SESSIONS) return false

  if (record.dismissed_at) {
    const cooldown = variant === 'chromium' ? CHROMIUM_COOLDOWN_MS : SAFARI_COOLDOWN_MS
    if (Date.now() - Date.parse(record.dismissed_at) < cooldown) return false
  }

  return true
}
